<script setup lang="ts">
// One interface's addresses (the machine panel's Network & time): the
// system's own (what the installer or the OS set), DHCP, or static, each
// said in a line. Addresses and DNS servers are lists checked as the node
// checks them (util cidrError, isIP), the gateway against the addresses'
// subnets, so Apply is only offered for settings the node takes. Applying
// puts the change on trial: the node goes back unless it is kept.
import { computed, ref } from 'vue'
import type { NetSettings } from '../../api'
import { cidrError, inSubnet, isIP, isIPv4 } from '../../util'
import ListInput from './ListInput.vue'

const props = defineProps<{ iface: string; current?: NetSettings; live: string[] }>()
const emit = defineEmits<{ save: [s: NetSettings | null]; cancel: [] }>()
type Mode = 'own' | 'dhcp' | 'static'
const modes: { id: Mode; label: string; says: string }[] = [
    { id: 'own', label: 'System default', says: 'Leave the interface as the OS has it (the installer’s setting).' },
    { id: 'dhcp', label: 'DHCP', says: 'An address from the network’s DHCP server; DNS servers optional.' },
    { id: 'static', label: 'Static', says: 'Fixed addresses with their prefix, a gateway and DNS servers.' },
]
const mode = ref<Mode>(!props.current ? 'own' : props.current.dhcp ? 'dhcp' : 'static')
// a static form starts from what the interface has now, when nothing was set here
const addresses = ref<string[]>(
    props.current?.addresses ?? (props.current ? [] : props.live.filter((a) => !cidrError(a)).slice(0, 4)),
)
const gateway = ref(props.current?.gateway ?? '')
const dns = ref<string[]>(props.current?.dns ?? [])
const gatewayError = computed(() => {
    const g = gateway.value.trim()
    if (!g || mode.value !== 'static') return ''
    if (!isIPv4(g)) return 'an IPv4 address, like 10.0.0.1'
    if (!addresses.value.some((a) => inSubnet(g, a))) return 'outside the addresses’ subnets'
    return ''
})
const invalid = computed(() => mode.value === 'static' && (!addresses.value.length || !!gatewayError.value))
function save() {
    if (mode.value === 'own') return emit('save', null)
    const s: NetSettings = { interface: props.iface, dns: dns.value }
    if (mode.value === 'dhcp') s.dhcp = true
    else Object.assign(s, { addresses: addresses.value, gateway: gateway.value.trim() || undefined })
    emit('save', s)
}
</script>

<template>
    <div
        class="my-1.5 rounded-md border border-line bg-bg-2 p-2.5 text-xs"
        role="group"
        :aria-label="`${iface} settings`"
    >
        <div
            class="flex w-full overflow-hidden rounded-md border border-line-strong"
            role="radiogroup"
            :aria-label="`${iface} addressing`"
        >
            <button
                v-for="m in modes"
                :key="m.id"
                type="button"
                role="radio"
                :aria-checked="mode === m.id"
                class="flex-1 rounded-none btn-ghost px-2 py-1"
                :class="{ 'bg-bg-3 text-fg': mode === m.id }"
                @click="mode = m.id"
            >
                {{ m.label }}
            </button>
        </div>
        <p class="mt-1.5 mb-2 text-fg-2">{{ modes.find((m) => m.id === mode)!.says }}</p>
        <div v-if="mode !== 'own'" class="grid gap-2">
            <div v-if="mode === 'static'">
                <label :for="`addr-${iface}`"
                    >Addresses <span class="text-fg-3">· one to four, with prefix</span></label
                >
                <ListInput
                    :id="`addr-${iface}`"
                    v-model="addresses"
                    :max="4"
                    :check="cidrError"
                    placeholder="10.0.0.5/24"
                />
            </div>
            <div v-if="mode === 'static'">
                <label :for="`gw-${iface}`">Gateway <span class="text-fg-3">· empty: this network only</span></label>
                <input :id="`gw-${iface}`" v-model="gateway" class="font-mono" placeholder="10.0.0.1" />
                <div v-if="gatewayError" class="mt-0.5 text-bad" role="alert">{{ gatewayError }}</div>
            </div>
            <div>
                <label :for="`dns-${iface}`"
                    >DNS servers
                    <span class="text-fg-3"
                        >· up to three{{ mode === 'dhcp' ? '; empty: the DHCP server’s' : '' }}</span
                    ></label
                >
                <ListInput
                    :id="`dns-${iface}`"
                    v-model="dns"
                    :max="3"
                    :check="(s) => (isIP(s) ? '' : 'an IP address, like 1.1.1.1')"
                    placeholder="1.1.1.1"
                />
            </div>
        </div>
        <p class="mt-2.5 rounded-md border border-line px-2 py-1.5 text-fg-2">
            ⚠ Applying changes {{ iface }} at once and starts a 2-minute trial: open the node at its new address (or
            through central) and press <b>Keep these settings</b>, or it goes back to the previous ones by itself.
        </p>
        <div class="mt-2 flex gap-2">
            <button class="btn-primary text-xs" :disabled="invalid" @click="save">Apply</button>
            <button class="btn-ghost text-xs" @click="emit('cancel')">Cancel</button>
        </div>
    </div>
</template>
